import { useEffect, useState } from 'react';
import { getView, loadPrism } from '../../engine/prism.js';
import { partAt, resolvePartPath, setPartField, type ModelDoc } from '../../lib/model-doc.js';
import { mirrorName } from '../../lib/rig/weights.js';
import { useModel } from '../../state/model.js';
import { Card, IconButton, NumberField, SelectField, TextField } from '../ui/controls.js';
import './rig-editor.css';

/** A part's blend shape (shape key), as stored in the model document. */
export interface BlendShape {
  name: string;
  ops?: unknown[];
  transform?: unknown;
  /** Sparse `[vertex, dx, dy, dz]` offsets of the finished mesh. */
  offsets?: [number, number, number, number][];
  relativeTo?: string;
  mirrorOf?: string;
}

/** An in-progress recording: the document before the edit. */
interface Recording {
  part: string;
  shape: string;
  before: ModelDoc;
}
let recording: Recording | null = null;

function preview(path: string, weights: Record<string, number>) {
  getView('model')?.modelCommand({ op: 'shapePreview', path, weights });
}

/** Adds `extra` offsets onto `shape`'s (sums per vertex). */
export function mergeOffsets(shape: BlendShape, extra: BlendShape['offsets'] = []): BlendShape {
  const sum = new Map<number, [number, number, number]>();
  for (const o of [...(shape.offsets ?? []), ...extra]) {
    const v = sum.get(o[0]) ?? [0, 0, 0];
    sum.set(o[0], [v[0] + o[1], v[1] + o[2], v[2] + o[3]]);
  }
  const offsets = [...sum]
    .filter(([, d]) => Math.hypot(...d) > 1e-7)
    .sort((a, b) => a[0] - b[0])
    .map(([i, d]) => [i, ...d] as [number, number, number, number]);
  const next = { ...shape };
  if (offsets.length) next.offsets = offsets;
  else delete next.offsets;
  return next;
}

/**
 * Shape keys of the selected part (Blender's Shape Keys panel): create,
 * record a mesh edit or sculpt into a shape, preview sliders, relative keys
 * and mirroring. Shapes become glTF morph targets that `expressions`
 * channels, animation clips and scripts drive by name.
 */
export function ShapeKeysPanel() {
  const doc = useModel((s) => s.doc),
    selected = useModel((s) => s.selected),
    update = useModel((s) => s.update);
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [, setTick] = useState(0);
  const [error, setError] = useState('');
  useEffect(() => {
    setWeights({});
    return () => {
      if (selected) preview(selected, {});
    };
  }, [selected]);
  if (!doc || !selected) return null;
  const index = resolvePartPath(doc, selected);
  const part = index ? partAt(doc, index) : null;
  if (!part || !index) return null;
  const shapes = (part.blendShapes as BlendShape[] | undefined) ?? [];
  const names = shapes.map((s) => s.name);
  const set = (next: BlendShape[], base: ModelDoc = doc) => {
    const at = resolvePartPath(base, selected);
    if (at) update(setPartField(base, at, 'blendShapes', next.length ? next : undefined));
  };
  const unique = (base: string) => {
    let n = base,
      i = 2;
    while (names.includes(n)) n = `${base} ${i++}`;
    return n;
  };
  const rec = recording?.part === selected ? recording : null;
  const start = (shape: string, sculpt: boolean) => {
    recording = { part: selected, shape, before: structuredClone(doc) };
    setError('');
    setTick((t) => t + 1);
    if (sculpt) useModel.getState().setSculpt(true);
  };
  const stop = async (store: boolean) => {
    const r = recording;
    recording = null;
    setTick((t) => t + 1);
    if (!r || !store) {
      if (r) update(r.before);
      return;
    }
    const current = useModel.getState().doc;
    if (!current) return;
    try {
      const prism = await loadPrism();
      if (!prism?.shapeFromEdit) throw new Error('Shape capture needs the current Prism Engine Cloud engine build. Rebuild the engine and reload.');
      const captured = JSON.parse(prism.shapeFromEdit(JSON.stringify(r.before), JSON.stringify(current), r.part, r.shape)) as BlendShape;
      // Revert the basis edit; the change lives in the shape.
      const at = resolvePartPath(r.before, r.part);
      const basePart = at ? partAt(r.before, at) : null;
      const list = ((basePart?.blendShapes as BlendShape[] | undefined) ?? []).slice();
      const i = list.findIndex((s) => s.name === r.shape);
      if (i >= 0) list[i] = mergeOffsets(list[i]!, captured.offsets);
      else list.push(captured);
      if (at) update(setPartField(r.before, at, 'blendShapes', list));
      setWeights((w) => ({ ...w, [r.shape]: 1 }));
      preview(r.part, { ...weights, [r.shape]: 1 });
    } catch (e) {
      update(r.before);
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const slide = (name: string, w: number) => {
    const next = { ...weights, [name]: w };
    setWeights(next);
    preview(selected, next);
  };
  return (
    <Card title="Shape keys" icon="sparkles" subtitle={shapes.length ? String(shapes.length) : undefined} defaultOpen={shapes.length > 0}>
      {rec ? (
        <div className="shape-recording" role="status">
          <span className="anim-record-dot" /> Recording into <strong>{rec.shape}</strong>: edit or sculpt the mesh (moving vertices only), then store.
          <div className="rig-button-row">
            <button type="button" className="primary small" onClick={() => void stop(true)}>
              Store in shape
            </button>
            <button type="button" className="ghost small" onClick={() => void stop(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="rig-button-row">
          <button type="button" className="ghost small" onClick={() => set([...shapes, { name: unique('Key') }])}>
            + New shape
          </button>
          <button type="button" className="ghost small" title="Record a mesh edit into a new shape" onClick={() => start(unique('Key'), false)}>
            Record mesh edit
          </button>
          <button type="button" className="ghost small" title="Sculpt into a new shape" onClick={() => start(unique('Key'), true)}>
            Sculpt into shape
          </button>
        </div>
      )}
      {error && <p className="rig-help danger">{error}</p>}
      {shapes.map((s, i) => (
        <div key={s.name} className="shape-key">
          <div className="shape-key-head">
            <TextField
              value={s.name}
              onChange={(name) => {
                if (!name.trim() || names.includes(name)) return;
                set(
                  shapes.map((x, j) =>
                    j === i
                      ? { ...x, name }
                      : { ...x, ...(x.relativeTo === s.name ? { relativeTo: name } : {}), ...(x.mirrorOf === s.name ? { mirrorOf: name } : {}) },
                  ),
                );
              }}
            />
            <IconButton icon="pencil" size={12} title="Record more edits into this shape" disabled={Boolean(rec)} onClick={() => start(s.name, false)} />
            <IconButton icon="sparkles" size={12} title="Sculpt into this shape" disabled={Boolean(rec)} onClick={() => start(s.name, true)} />
            <IconButton
              icon="mirror"
              size={12}
              title="Create the mirrored shape (.L ↔ .R across X)"
              onClick={() => {
                const m = mirrorName(s.name);
                set([...shapes, { name: unique(m !== s.name ? m : `${s.name} mirrored`), mirrorOf: s.name }]);
              }}
            />
            <IconButton
              icon="trash"
              size={12}
              title="Delete shape"
              onClick={() => set(shapes.filter((_, j) => j !== i).map((x) => (x.relativeTo === s.name || x.mirrorOf === s.name ? { ...x, relativeTo: undefined, mirrorOf: undefined } : x)))}
            />
          </div>
          <div className="shape-key-row">
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={weights[s.name] ?? 0}
              aria-label={`${s.name} preview weight`}
              onChange={(e) => slide(s.name, Number(e.target.value))}
            />
            <NumberField value={weights[s.name] ?? 0} min={-1} max={2} step={0.05} onChange={(v) => slide(s.name, v)} />
          </div>
          <div className="shape-key-row">
            <span className="muted">Relative to</span>
            <SelectField
              value={s.relativeTo ?? ''}
              options={[{ value: '', label: 'Basis' }, ...names.slice(0, i).map((n) => ({ value: n, label: n }))]}
              onChange={(v) => set(shapes.map((x, j) => (j === i ? { ...x, relativeTo: v || undefined } : x)))}
            />
            {s.mirrorOf && <span className="muted">mirror of {s.mirrorOf}</span>}
            {s.offsets?.length ? <span className="muted">{s.offsets.length} verts</span> : null}
          </div>
        </div>
      ))}
      {shapes.length > 0 && (
        <p className="muted rig-help">
          Sliders only preview. In a scene, drive shapes by name from an <code>expressions</code> component, animation clips or scripts.
        </p>
      )}
    </Card>
  );
}
